// @ts-strict-ignore
import React, {
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  AdvancedMenuContent,
  Icon,
  IconName,
} from '@usealloy/component-library';
import type { AdvancedMenuItemType } from '@usealloy/component-library';
import { AggregationEditorContext } from './context';
import type { AggregationFilter } from 'flow/store/engine/types';
import { AggregationFilterOperatorType } from 'flow/store/engine/constants';
import { StyledFilterTriggerButton, StyledFilterDropdownMenu } from './styles';
import AggregationFilterRow from './AggregationFilterRow';

const AggregationFiltersSection = () => {
  const {
    sourceData, setSourceData, attribute,
  } = useContext(AggregationEditorContext);

  const [menuIsOpen, setMenuIsOpen] = useState(false);

  const filterOptions = useMemo(
    () => attribute?.aggregationFilterOptions ?? [],
    [attribute?.aggregationFilterOptions],
  );

  const handleFilterToggle = useCallback((columnSlug: string, checked: boolean) => {
    if (checked) {
      const newFilter: AggregationFilter = {
        columnSlug,
        operatorSlug: AggregationFilterOperatorType.EQ,
        staticValue: '',
      };
      setSourceData(prev => ({
        ...prev,
        filters: [...prev.filters, newFilter],
      }));
    } else {
      setSourceData(prev => ({
        ...prev,
        filters: prev.filters.filter(f => f.columnSlug !== columnSlug),
      }));
    }
  }, [setSourceData]);

  const handleUpdateFilter = useCallback((
    columnSlug: string,
    updates: Partial<AggregationFilter>,
  ) => {
    setSourceData(prev => ({
      ...prev,
      filters: prev.filters.map(f => (
        f.columnSlug === columnSlug ? { ...f, ...updates } : f
      )),
    }));
  }, [setSourceData]);

  const handleRemoveFilter = useCallback((columnSlug: string) => {
    setSourceData(prev => ({
      ...prev,
      filters: prev.filters.filter(f => f.columnSlug !== columnSlug),
    }));
  }, [setSourceData]);

  const menuItems: AdvancedMenuItemType[] = useMemo(
    () => filterOptions.map(option => {
      const isActive = sourceData.filters.some(f => f.columnSlug === option.value);
      return {
        value: option.value,
        label: option.label,
        showCheckbox: true,
        checked: isActive,
        closeOnSelect: false,
        onCheckboxChange: (checked: boolean) => handleFilterToggle(option.value, checked),
        onSelect: () => handleFilterToggle(option.value, !isActive),
      };
    }),
    [filterOptions, sourceData.filters, handleFilterToggle],
  );

  if (filterOptions.length === 0) return null;

  return (
    <>
      {sourceData.filters.map(filter => {
        const filterOption = filterOptions.find(o => o.value === filter.columnSlug);
        return (
          <AggregationFilterRow
            key={filter.columnSlug}
            filter={filter}
            filterOption={filterOption}
            onUpdate={handleUpdateFilter}
            onRemove={handleRemoveFilter}
          />
        );
      })}

      <StyledFilterDropdownMenu
        isOpen={menuIsOpen}
        onOpenChange={setMenuIsOpen}
        align="start"
        trigger={(
          <StyledFilterTriggerButton>
            Add/Remove Filter
            <Icon icon={menuIsOpen ? IconName.CaretUp : IconName.CaretDown} size="small" />
          </StyledFilterTriggerButton>
        )}
      >
        <AdvancedMenuContent
          items={menuItems}
          enableMainFilter
          mainFilterPlaceholder="Search"
          emptyMessage="No filters available."
          dynamicMenuHeight
          onClose={() => setMenuIsOpen(false)}
        />
      </StyledFilterDropdownMenu>
    </>
  );
};

export default AggregationFiltersSection;
